import FeatureImageWithBlur from "@/components/us-website/FeatureImageWithBlur/FeatureImageWithBlur";
import IndustryExpertise from "@/components/us-website/IndustryExpertise/IndustryExpertise";
import IndustryList from "@/components/us-website/IndustryList/IndustryList";
import React from "react";
import ContactUsForm from "@/components/us-website/ContactUsForm/ContactUsForm";
import MetaTags from "@/components/seo/meta-tags";
import { getCDNImageUrl } from "@/utils/Common";
import { getIndustriesTopProductsServerSide } from "@/api/ProductApi";
import { getIndustryListServerSide } from "@/api/ProductApi";
import { useRouter } from "next/router";
import LocationWrapper from "@/components/us-website/LocationWrapper";
import useProductSearch from "../../hooks/useProductSearch";

// Skeletal loading component for industry list
const SkeletalIndustryList = () => {
  return (
    <div style={{ padding: "2rem", opacity: 0.6 }}>
      <div
        style={{
          height: "20px",
          backgroundColor: "#e0e0e0",
          borderRadius: "4px",
          width: "200px",
          marginBottom: "1rem",
        }}
      ></div>
      <div
        style={{
          height: "40px",
          backgroundColor: "#e0e0e0",
          borderRadius: "4px",
          width: "400px",
          marginBottom: "2rem",
        }}
      ></div>
      <div style={{ display: "flex", gap: "1rem", overflowX: "auto" }}>
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              minWidth: "300px",
              height: "400px",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              padding: "1rem",
            }}
          >
            <div
              style={{
                height: "200px",
                backgroundColor: "#e0e0e0",
                borderRadius: "4px",
                marginBottom: "1rem",
              }}
            ></div>
            <div
              style={{
                height: "20px",
                backgroundColor: "#e0e0e0",
                borderRadius: "4px",
                marginBottom: "0.5rem",
              }}
            ></div>
            <div
              style={{
                height: "60px",
                backgroundColor: "#e0e0e0",
                borderRadius: "4px",
                marginBottom: "1rem",
              }}
            ></div>
          </div>
        ))}
      </div>
    </div>
  );
};

function NewIndustriesListing({
  industriesData = [],
  error = null,
  industryList = [],
}) {
  const router = useRouter();
  const { searchProps } = useProductSearch();

  return (
    <LocationWrapper>
      <MetaTags
        title="Chemical Solutions for US Industries | Elchemy"
        description="Serving key US industries - Personal Care, Food, Water Treatment, Oil & Gas, CASE & more,with high quality specialty chemicals."
      />
      {/* Feature Image with Blur Below */}
      <FeatureImageWithBlur
        heading="Solutions Tailored to Your Industry"
        description="From personal care to flavors and fragrances, Elchemy supplies high-quality specialty and industrial chemicals with a strong local presence."
        imageSrc={getCDNImageUrl("industries-listing-hero.webp")}
        showSearchBar={true}
        searchProps={searchProps}
      />
      {/* Industry Expertise */}
      <IndustryExpertise
        subheading="BROWSE BY"
        heading="Our Industry Expertise"
        industryList={industryList}
      />

      {/* Error State */}
      {error && (
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            color: "#ff6b6b",
          }}
        >
          <h3>Error loading industries data</h3>
          <p>{error}</p>
        </div>
      )}

      {/* Industry Lists */}
      {!error && industriesData?.length > 0 && (
        <div>
          {industriesData.map((industry, index) => (
            <IndustryList
              index={index}
              key={industry?.industry_id}
              industryId={industry?.industry_id}
              industryName={industry?.industry_name}
              products={industry?.products}
            />
          ))}
        </div>
      )}

      {/* No Data State */}
      {!error && industriesData?.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "4rem 2rem",
            color: "#666",
          }}
        >
          <h3>No industries data available</h3>
          <p>Please check back later for industry listings and products.</p>
        </div>
      )}
      <ContactUsForm />
    </LocationWrapper>
  );
}

export async function getServerSideProps() {
  try {
    const [industriesData, industryListData] = await Promise.all([
      getIndustriesTopProductsServerSide(),
      getIndustryListServerSide(),
    ]);

    if (industriesData) {
      // Define the desired order of industries
      const industryOrder = [
        { id: 3, title: "Personal Care" },
        { id: 6, title: "Home Care and Industrial Cleaning" },
        { id: 5, title: "Flavours and Fragrance" },
        { id: 2, title: "Oil and Gas" },
        { id: 4, title: "Food and Nutrition" },
        { id: 7, title: "Chemical Intermediates" },
        { id: 1, title: "Water Treatment & Mining" },
        { id: 8, title: "C.A.S.E." },
      ];

      // Sort the industries data according to the specified order
      const sortedIndustriesData = industryOrder
        .map((orderItem) => {
          return industriesData.find(
            (industry) => industry.industry_id === orderItem.id
          );
        })
        .filter((industry) => industry !== undefined); // Remove any industries not found in the data

      return {
        props: {
          industriesData: sortedIndustriesData,
          error: null,
          industryList: industryListData?.industry_name || [],
        },
      };
    } else {
      return {
        props: {
          industriesData: [],
          error: "Failed to fetch industries data",
          industryList: industryListData?.industry_name || [],
        },
      };
    }
  } catch (error) {
    console.error("Error in getServerSideProps:", error);
    return {
      props: {
        industriesData: [],
        error: "An error occurred while fetching data",
        industryList: [],
      },
    };
  }
}

export default NewIndustriesListing;
